import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Sparkles, Upload, FileSpreadsheet, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IRS_CATEGORIES = [
  { value: 'advertising', label: 'Advertising' },
  { value: 'car_and_truck', label: 'Car & Truck Expenses' },
  { value: 'commissions', label: 'Commissions & Fees' },
  { value: 'contract_labor', label: 'Contract Labor' },
  { value: 'depreciation', label: 'Depreciation' },
  { value: 'employee_benefits', label: 'Employee Benefits' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'interest_mortgage', label: 'Interest - Mortgage' },
  { value: 'interest_other', label: 'Interest - Other' },
  { value: 'legal_professional', label: 'Legal & Professional' },
  { value: 'office_expense', label: 'Office Expense' },
  { value: 'pension', label: 'Pension Plans' },
  { value: 'rent_vehicles', label: 'Rent - Vehicles/Equipment' },
  { value: 'rent_property', label: 'Rent - Property' },
  { value: 'repairs', label: 'Repairs & Maintenance' },
  { value: 'supplies', label: 'Supplies' },
  { value: 'taxes_licenses', label: 'Taxes & Licenses' },
  { value: 'travel', label: 'Travel' },
  { value: 'meals', label: 'Meals (50%)' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'wages', label: 'Wages' },
  { value: 'other_expense', label: 'Other Expenses' },
  { value: 'home_office', label: 'Home Office' },
  { value: 'health_insurance', label: 'Health Insurance' },
];

const EMPTY_MAPPING = { date_column: '', description_column: '', amount_column: '', category_column: '', type_column: '', notes_column: '' };

const getIrsLine = (cat) => {
  const map = {
    advertising: 'Line 8', car_and_truck: 'Line 9', commissions: 'Line 10', contract_labor: 'Line 11',
    depreciation: 'Line 13', employee_benefits: 'Line 14', insurance: 'Line 15',
    interest_mortgage: 'Line 16a', interest_other: 'Line 16b', legal_professional: 'Line 17',
    office_expense: 'Line 18', pension: 'Line 19', rent_vehicles: 'Line 20a', rent_property: 'Line 20b',
    repairs: 'Line 21', supplies: 'Line 22', taxes_licenses: 'Line 23', travel: 'Line 24a',
    meals: 'Line 24b', utilities: 'Line 25', wages: 'Line 26', other_expense: 'Line 27a',
    home_office: 'Form 8829', health_insurance: 'Schedule 1',
  };
  return map[cat] || 'Line 27a';
};

export const CsvImportDialog = ({ open, onClose, onImportComplete }) => {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const fileRef = useRef(null);
  const [step, setStep] = useState(1);
  const [importYear, setImportYear] = useState(new Date().getFullYear());
  const [previewData, setPreviewData] = useState(null);
  const [csvContent, setCsvContent] = useState('');
  const [mapping, setMapping] = useState(EMPTY_MAPPING);
  const [catMappings, setCatMappings] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [importing, setImporting] = useState(false);

  const setMap = (k, v) => setMapping(m => ({ ...m, [k]: v }));
  const years = Array.from({ length: 7 }, (_, i) => new Date().getFullYear() - 5 + i);

  const reset = () => {
    setStep(1); setPreviewData(null); setCsvContent(''); setMapping(EMPTY_MAPPING);
    setCatMappings([]); setLoadingSuggestions(false);
    if (fileRef.current) fileRef.current.value = '';
  };
  const handleOpenChange = (isOpen) => { if (!isOpen) { reset(); onClose(); } };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) { toast.error(t('toast.please_select_a_csv_file')); return; }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/csv/preview`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }, withCredentials: true
      });
      setPreviewData(res.data);
      setCsvContent(res.data.csv_content);
      // Auto-detect
      const nm = { ...EMPTY_MAPPING };
      res.data.headers.forEach(h => {
        const hl = h.toLowerCase();
        if (hl.includes('date') && !nm.date_column) nm.date_column = h;
        else if ((hl.includes('desc') || hl.includes('memo') || hl.includes('name') || hl.includes('payee')) && !nm.description_column) nm.description_column = h;
        else if ((hl.includes('amount') || hl.includes('total') || hl === 'debit' || hl === 'credit') && !nm.amount_column) nm.amount_column = h;
        else if ((hl.includes('category') || hl.includes('type') || hl.includes('class')) && !nm.category_column) nm.category_column = h;
        else if ((hl.includes('note') || hl.includes('comment')) && !nm.notes_column) nm.notes_column = h;
      });
      setMapping(nm);
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || t('toast.failed_to_read_csv')); }
  };

  const handleMappingNext = async () => {
    if (!mapping.date_column || !mapping.amount_column) {
      toast.error(t('toast.please_map_at_least_date_and_amount_columns')); return;
    }
    if (mapping.category_column && previewData?.headers.includes(mapping.category_column)) {
      const unique = [...new Set(previewData.sample_rows.map(r => r[mapping.category_column]).filter(Boolean))];
      if (unique.length > 0) {
        setLoadingSuggestions(true);
        try {
          const res = await axios.post(`${API}/csv/suggest-categories`, { categories: unique }, { headers: getAuthHeaders(), withCredentials: true });
          setCatMappings(res.data.suggestions.map(s => ({ original: s.original, mapped_to: s.mapped_to, irs_line: s.irs_line })));
        } catch {
          setCatMappings(unique.map(cat => ({ original: cat, mapped_to: 'other_expense', irs_line: 'Line 27a' })));
        } finally { setLoadingSuggestions(false); }
        setStep(3); return;
      }
    }
    setStep(4);
  };

  const handleCatChange = (orig, val) => setCatMappings(prev => prev.map(m => m.original === orig ? { ...m, mapped_to: val, irs_line: getIrsLine(val) } : m));

  const handleImport = async () => {
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('csv_content', csvContent);
      fd.append('column_mapping', JSON.stringify(mapping));
      fd.append('year', importYear);
      const res = await axios.post(`${API}/csv/import`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }, withCredentials: true
      });
      const { count, errors } = res.data;
      if (count > 0) toast.success(t('toast.extracted_count', { count }));
      if (errors?.length > 0) toast.warning(`${errors.length} rows had errors and were skipped`);
      reset(); onClose(); onImportComplete?.();
    } catch (e) { toast.error(e.response?.data?.detail || t('toast.failed_to_import_csv')); }
    finally { setImporting(false); }
  };

  const ColSelect = ({ label, field, required }) => (
    <div className="space-y-2">
      <Label className="text-gray-700 flex items-center gap-1">{label}{required && <span className="text-red-500">*</span>}</Label>
      <Select value={mapping[field] || (required ? '' : 'none')} onValueChange={v => setMap(field, v === 'none' ? '' : v)}>
        <SelectTrigger className="bg-white border-gray-300"><SelectValue placeholder={required ? 'Select column' : 'Optional'} /></SelectTrigger>
        <SelectContent>
          {!required && <SelectItem value="none">None</SelectItem>}
          {(previewData?.headers || []).map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            {t('transactions.import_csv_step', 'Import CSV — Step {{step}} of 4', { step })}
          </DialogTitle>
          <DialogDescription>
            {step === 1 && t('transactions.csv_step1_desc', 'Upload your CSV file')}
            {step === 2 && t('transactions.csv_step2_desc', 'Map your CSV columns to transaction fields')}
            {step === 3 && t('transactions.csv_step3_desc', 'Map categories to IRS Schedule C')}
            {step === 4 && t('transactions.csv_step4_desc', 'Review and import')}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-4 mb-2">
              <Label>Import Year:</Label>
              <Select value={importYear.toString()} onValueChange={v => setImportYear(parseInt(v))}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>{years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary transition-colors">
              <input type="file" ref={fileRef} accept=".csv" onChange={handleFile} className="hidden" id="csv-file-input" />
              <label htmlFor="csv-file-input" className="cursor-pointer">
                <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-lg font-medium text-gray-700">Click to upload CSV file</p>
                <p className="text-sm text-gray-500 mt-1">Google Sheets, Excel exports, bank statements</p>
              </label>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
              <strong>Tip:</strong> Export from Google Sheets using File → Download → CSV
            </div>
          </div>
        )}

        {step === 2 && previewData && (
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <strong>Required:</strong> Date and Amount columns must be mapped.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ColSelect label="Date Column" field="date_column" required />
              <ColSelect label="Amount Column" field="amount_column" required />
              <ColSelect label="Description Column" field="description_column" />
              <ColSelect label="Category Column" field="category_column" />
              <ColSelect label="Type Column" field="type_column" />
              <ColSelect label="Notes Column" field="notes_column" />
            </div>
            <div className="mt-4">
              <Label className="text-gray-700 mb-2 block">Preview (first 5 rows)</Label>
              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>{previewData.headers.map((h, i) => <th key={i} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>)}</tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {previewData.sample_rows.map((row, ri) => (
                      <tr key={ri}>{previewData.headers.map((h, ci) => <td key={ci} className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">{row[h]}</td>)}</tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-2">Total rows: {previewData.row_count}</p>
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={handleMappingNext}>{mapping.category_column ? 'Next: Map Categories' : 'Skip to Import'}</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 py-4">
            {loadingSuggestions ? (
              <div className="flex items-center justify-center py-8">
                <Sparkles className="w-6 h-6 animate-pulse text-primary mr-2" />
                <span className="text-gray-600">AI is analyzing your categories...</span>
              </div>
            ) : (
              <>
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />AI has suggested IRS category mappings. Review and adjust as needed.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {catMappings.map((m, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 truncate">{m.original}</p>
                        <p className="text-xs text-gray-500">{m.irs_line}</p>
                      </div>
                      <Select value={m.mapped_to} onValueChange={v => handleCatChange(m.original, v)}>
                        <SelectTrigger className="w-44 bg-white border-gray-300"><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {IRS_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
                  <Button onClick={() => setStep(4)}>Next: Review & Import</Button>
                </div>
              </>
            )}
          </div>
        )}

        {step === 4 && previewData && (
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <h4 className="font-medium text-gray-800">Import Summary</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <p className="text-gray-600">Total Rows:</p><p className="font-medium">{previewData.row_count}</p>
                <p className="text-gray-600">Year:</p><p className="font-medium">{importYear}</p>
                <p className="text-gray-600">Categories Mapped:</p><p className="font-medium">{catMappings.length}</p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              All transactions will be imported as <strong>Draft</strong>. Review and finalize after import.
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(catMappings.length > 0 ? 3 : 2)}>Back</Button>
              <Button onClick={handleImport} disabled={importing} data-testid="csv-import-submit">
                {importing
                  ? <><Sparkles className="w-4 h-4 mr-2 animate-spin" />Importing...</>
                  : <><Check className="w-4 h-4 mr-2" />Import {previewData.row_count} Transactions</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
